import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/comments-widget-registry',
        destPath: '/v1/comments/widgets',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
      {
        srcPath: '/_api/comments/widgets/v1',
        destPath: '/v1/comments/widgets',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
      {
        srcPath: '/_api/comments/widgets/v1',
        destPath: '/v1/comments/widgets',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
      {
        srcPath: '/_api/comments/widgets/v1',
        destPath: '/v1/comments/widgets',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/comments-widget-registry/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/api/v1/comments/widgets',
        destPath: '/v1/comments/widgets',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/comments-widget-registry',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_comments_widgets';

/** Registers CommentsWidget. Whenever a widget is loaded for the first time or its resourceId changes, it saves the new resource information about the widget */
export function registerCommentsWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __registerCommentsWidget({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'POST' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.RegisterCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/register',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'commentsWidget.createdDate' },
              { path: 'commentsWidget.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __registerCommentsWidget;
}

/** Returns all registered widget resource ids contained in metaSite */
export function queryCommentsWidgetResources(
  payload: object
): RequestOptionsFactory<any> {
  function __queryCommentsWidgetResources({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'POST' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.QueryCommentsWidgetResources',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/resources/query',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __queryCommentsWidgetResources;
}

/** Creates a new CommentsWidget */
export function createCommentsWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __createCommentsWidget({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'commentsWidget.createdDate' },
          { path: 'commentsWidget.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'POST' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.CreateCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'commentsWidget.createdDate' },
              { path: 'commentsWidget.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCommentsWidget;
}

/** Get a CommentsWidget by id */
export function getCommentsWidget(payload: object): RequestOptionsFactory<any> {
  function __getCommentsWidget({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'GET' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.GetCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/{commentsWidgetId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'commentsWidget.createdDate' },
              { path: 'commentsWidget.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCommentsWidget;
}

/**
 * Update CommentsWidget, supports partial update
 * Pass the latest `revision` for a successful update
 */
export function updateCommentsWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCommentsWidget({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'commentsWidget.createdDate' },
          { path: 'commentsWidget.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'PATCH' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.UpdateCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/{commentsWidget.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'commentsWidget.createdDate' },
              { path: 'commentsWidget.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCommentsWidget;
}

/** Delete a CommentsWidget */
export function deleteCommentsWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCommentsWidget({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'DELETE' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.DeleteCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/{commentsWidgetId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCommentsWidget;
}

/** Query CommentsWidgets using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) */
export function queryCommentsWidget(
  payload: object
): RequestOptionsFactory<any> {
  function __queryCommentsWidget({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.comments.categories.v1.comments_widget',
      method: 'POST' as any,
      methodFqn:
        'wix.comments.categories.v1.CommentsWidgetRegistry.QueryCommentsWidget',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixCommentsCategoriesV1CommentsWidgetRegistryUrl({
        protoPath: '/v1/comments/widgets/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'commentsWidgets.createdDate' },
              { path: 'commentsWidgets.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryCommentsWidget;
}
